import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useUserDisplayName } from "@/lib/liveblocks/useUserDisplayName";
import { cn } from "@/utils";
import { getInitials } from "@cohesive/ui-components";
import { useUser } from "@liveblocks/react";
import { Timestamp } from "@liveblocks/react-ui/primitives";
import { Fragment } from "react";
import type { CommentPreview, PreviewSegment } from "./commentPreview";

/** Screen-px width of the expanded pin — a glance, narrower than the thread. */
const CARD_WIDTH = 256;

/**
 * A collapsed pin, expanded: the thread's opening comment as a card. One
 * component for both moments it appears — the hover preview and the card a
 * dragged pin wears — so the two can never drift.
 *
 * Neutral paper and ink, avatar first. Rust is the acting/agent color
 * (`docs/design/hover-interactive-feedback.md` § Signal colors); a comment is
 * people talking, so it takes the card register instead.
 */
export function CanvasCommentPreviewCard({
  preview,
  className,
}: {
  preview: CommentPreview;
  className?: string;
}) {
  const { user } = useUser(preview.userId);

  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg border border-n-10 bg-popover p-2 text-left shadow-elev-3",
        className,
      )}
      style={{ width: CARD_WIDTH }}
    >
      <CanvasCommentIdentityAvatar
        name={user?.name}
        src={user?.avatar}
        className="size-8"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1 text-sm">
          <span className="truncate font-medium text-n-90">
            {user?.name ?? "…"}
          </span>
          <span className="text-n-40">·</span>
          <Timestamp
            date={preview.createdAt}
            className="shrink-0 text-n-50 tabular-nums"
          />
        </div>
        {preview.segments.length > 0 && (
          <p className="mt-0.5 line-clamp-3 text-base leading-normal text-n-70">
            <CanvasCommentPreviewText segments={preview.segments} />
          </p>
        )}
        {preview.replyCount > 0 && (
          <p className="mt-1 text-sm text-n-50">
            {preview.replyCount === 1
              ? "1 reply"
              : `${preview.replyCount} replies`}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The preview body: text as it was written, mentions as the names they point
 * at. A mention whose id resolves to nobody reads as `@user` — a raw uuid is
 * noise, and the thread underneath still carries the truth.
 */
function CanvasCommentPreviewText({
  segments,
}: {
  segments: PreviewSegment[];
}) {
  const displayName = useUserDisplayName();

  return (
    <>
      {segments.map((segment, index) => (
        <Fragment key={index}>
          {segment.type === "mention" ? (
            <span className="font-medium text-n-90">
              @{displayName(segment.userId) ?? "user"}
            </span>
          ) : (
            segment.text
          )}
        </Fragment>
      ))}
    </>
  );
}

/** A comment author's avatar, resolved from their id. */
export function CanvasCommentAvatar({
  userId,
  className,
}: {
  userId: string;
  className?: string;
}) {
  const { user } = useUser(userId);
  return (
    <CanvasCommentIdentityAvatar
      name={user?.name}
      src={user?.avatar}
      className={className}
    />
  );
}

/**
 * The avatar every comment surface wears — pin, card, draft, ghost.
 * `draggable={false}` is the point: without it the browser's native image drag
 * fires on pointer-down and steals the pin's drag gesture.
 */
export function CanvasCommentIdentityAvatar({
  name,
  src,
  className,
}: {
  name?: string;
  src?: string;
  className?: string;
}) {
  return (
    <Avatar className={cn("bg-n-10", className)}>
      <AvatarImage src={src} alt={name} draggable={false} />
      <AvatarFallback className="bg-n-10 text-2xs font-medium text-n-60">
        {getInitials(name ?? "")}
      </AvatarFallback>
    </Avatar>
  );
}
